import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Upload, X, GripVertical, Image, Film, FileText, Trash2, Eye, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.startsWith('video/')) return Film;
  return FileText;
};

export const MediaGallery = ({ 
  items = [], 
  onItemsChange, 
  token, 
  folder = 'general',
  maxItems = 20,
  acceptedTypes = ['image/*', 'video/*', '.pdf'],
  showReorder = true 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer?.files || e.target?.files || []);
    if (files.length === 0) return;
    
    if (items.length + files.length > maxItems) {
      toast.error(`Máximo ${maxItems} archivos permitidos`);
      return;
    }

    setUploading(true);
    const newItems = [...items];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload?folder=${folder}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        newItems.push({
          id: response.data.file_id,
          url: `${API.replace('/api', '')}${response.data.url}`,
          path: response.data.path,
          filename: file.name,
          contentType: file.type,
          size: file.size
        });
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Error al subir ${file.name}`);
      }
    }

    onItemsChange(newItems);
    setUploading(false);
    setUploadProgress(0);
    toast.success(`${files.length} archivo(s) subido(s)`);
  }, [items, maxItems, token, folder, onItemsChange]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onItemsChange(newItems);
    toast.success('Archivo eliminado');
  };

  const handleReorder = (newOrder) => {
    onItemsChange(newOrder);
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragOver 
            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
            : 'border-white/10 hover:border-white/20'
        }`}
      >
        <input
          type="file"
          accept={acceptedTypes.join(',')}
          multiple
          onChange={handleDrop}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="py-4">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
            <p className="text-white/60">Subiendo... {uploadProgress}%</p>
            <div className="w-48 h-1 mx-auto mt-4 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#D4AF37] transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <Upload size={40} className={`mx-auto mb-4 ${dragOver ? 'text-[#D4AF37]' : 'text-white/30'}`} />
            <p className="text-white/60 mb-2">
              Arrastra archivos aquí o haz clic para seleccionar
            </p>
            <p className="text-white/40 text-sm">
              Formatos: JPG, PNG, WEBP, MP4, PDF • Máximo 50MB por archivo
            </p>
            <p className="text-white/30 text-xs mt-2">
              {items.length} / {maxItems} archivos
            </p>
          </>
        )}
      </div>

      {/* Gallery Grid */}
      {items.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm">
              {items.length} archivo{items.length !== 1 ? 's' : ''}
            </p>
            {showReorder && items.length > 1 && (
              <p className="text-white/40 text-xs flex items-center gap-1">
                <GripVertical size={14} />
                Arrastra para reordenar
              </p>
            )}
          </div>

          {showReorder ? (
            <Reorder.Group 
              axis="y" 
              values={items} 
              onReorder={handleReorder}
              className="space-y-3"
            >
              {items.map((item, index) => (
                <Reorder.Item
                  key={item.id || item.url || index}
                  value={item}
                  className="flex items-center gap-4 p-3 glass-dark rounded-xl group cursor-grab active:cursor-grabbing"
                >
                  <GripVertical size={18} className="text-white/30 group-hover:text-white/60" />
                  
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/50 flex-shrink-0">
                    {item.contentType?.startsWith('image/') || item.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                      <img 
                        src={item.url} 
                        alt={item.filename} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {React.createElement(getFileIcon(item.contentType), { 
                          size: 24, 
                          className: 'text-white/40' 
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{item.filename || 'Archivo'}</p>
                    <p className="text-white/40 text-xs">
                      {item.size ? `${(item.size / 1024 / 1024).toFixed(2)} MB` : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setPreviewItem(item)}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => removeItem(index)}
                      className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {items.map((item, index) => (
                <div
                  key={item.id || item.url || index}
                  className="relative aspect-square rounded-xl overflow-hidden bg-black/50 group"
                >
                  {item.contentType?.startsWith('image/') || item.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                    <img 
                      src={item.url} 
                      alt={item.filename} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {React.createElement(getFileIcon(item.contentType), { 
                        size: 32, 
                        className: 'text-white/40' 
                      })}
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => setPreviewItem(item)}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => removeItem(index)}
                      className="p-2 rounded-full bg-red-500/50 hover:bg-red-500/70 text-white transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {previewItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setPreviewItem(null)}
          >
            <button
              onClick={() => setPreviewItem(null)}
              className="absolute top-6 right-6 text-white/60 hover:text-white"
            >
              <X size={32} />
            </button>
            
            {previewItem.contentType?.startsWith('image/') || previewItem.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
              <img 
                src={previewItem.url} 
                alt={previewItem.filename}
                className="max-h-[90vh] max-w-[90vw] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            ) : previewItem.contentType?.startsWith('video/') ? (
              <video 
                src={previewItem.url}
                controls
                className="max-h-[90vh] max-w-[90vw]"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="glass-dark rounded-xl p-12 text-center" onClick={(e) => e.stopPropagation()}>
                <FileText size={64} className="mx-auto text-white/40 mb-4" />
                <p className="text-white">{previewItem.filename}</p>
                <a 
                  href={previewItem.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary mt-4 inline-block"
                >
                  Abrir archivo
                </a>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Simple image URL input for gallery management (when images are external URLs)
export const ImageUrlGallery = ({ 
  urls = [], 
  onUrlsChange, 
  maxItems = 20 
}) => {
  const [newUrl, setNewUrl] = useState('');

  const addUrl = () => {
    if (!newUrl.trim()) return;
    if (urls.length >= maxItems) {
      toast.error(`Máximo ${maxItems} imágenes permitidas`);
      return;
    }
    onUrlsChange([...urls, newUrl.trim()]);
    setNewUrl('');
    toast.success('URL agregada');
  };

  const removeUrl = (index) => {
    onUrlsChange(urls.filter((_, i) => i !== index));
    toast.success('Imagen eliminada');
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="input-premium flex-1"
          onKeyDown={(e) => e.key === 'Enter' && addUrl()}
        />
        <button
          onClick={addUrl}
          disabled={!newUrl.trim()}
          className="btn-secondary px-4"
        >
          Agregar
        </button>
      </div>

      {urls.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {urls.map((url, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-black/50 group">
              <img 
                src={url} 
                alt={`Gallery ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/200?text=Error'; }}
              />
              <button
                onClick={() => removeUrl(index)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
